import { PureAbility, AbilityBuilder } from '@casl/ability';
import { LruCache } from './lru-cache.js';
/**
 * Map wildcard '*' to 'manage' - CASL treats 'manage' as matching all actions
 */
const caslOperation = (operation) => {
    return operation === '*' ? 'manage' : operation;
};
const MAX_CACHE_SIZE = 1000;
/**
 * Placeholder location key auth-service emits in `rolesByLocation` for grants
 * that span every location (internal `pb:super` users, cross-brand service
 * clients). Its presence means "member of all locations"; mirrors
 * `SYNTHETIC_LOCATION_ID` in auth-service's `user-access/constants.ts`.
 */
const SYNTHETIC_LOCATION_ID = '*';
/**
 * Owner's entrypoint for managing user permissions and role-based access control
 *
 * The Authorizer class provides a flexible permission system with support for:
 * - Role-based access control (RBAC)
 * - Location-specific permissions
 * - Multiple authorization providers (Mock, Auth Service)
 * - Automatic permission refresh
 *
 * @example
 * ```typescript
 * const mockData = {
 *   userId: 'user123',
 *   email: 'user@example.com',
 *   brandId: 'brand456',
 *   brandRoles: ['brand:manager'],
 *   permissionTree: {
 *     rolesByLocation: { 'loc1': ['location-admin'] },
 *     permissionsByRole: {
 *       'brand:manager': ['perm1', 'perm2'],
 *       'location-admin': ['perm3']
 *     },
 *     permissionsById: {
 *       perm1: { resource: 'order', operation: 'read' },
 *       perm2: { resource: 'order', operation: 'update' },
 *       perm3: { resource: 'order', operation: 'delete' }
 *     }
 *   }
 * }
 *
 * const authorizer = new Authorizer({
 *   provider: new MockAuthorizationProvider(mockData),
 *   autoRefresh: true,
 *   refreshInterval: 300
 * })
 *
 * await authorizer.initialize()
 *
 * if (authorizer.can('read', 'order')) {
 *   // User has permission to read orders
 * }
 * ```
 */
export class Authorizer {
    ability;
    autoRefresh;
    enforceLocationScope;
    locationAbilitiesCache;
    onLocationScopeDivergence;
    permissionTree;
    provider;
    refreshInterval;
    refreshIntervalSeconds;
    /**
     * Creates a new Authorizer instance
     *
     * @param config - Configuration object for the authorizer
     * @param config.provider - The authorization provider to use for fetching permissions
     * @param config.autoRefresh - Enable automatic background refresh of permissions (default: false)
     * @param config.refreshInterval - Refresh interval in seconds (default: 300)
     * @param config.maxCacheSize - Maximum number of location-specific ability entries to cache (default: 1000)
     */
    constructor(config) {
        this.provider = config.provider;
        this.locationAbilitiesCache = new LruCache(config.maxCacheSize ?? MAX_CACHE_SIZE);
        this.autoRefresh = config?.autoRefresh ?? false;
        this.enforceLocationScope = config?.enforceLocationScope ?? false;
        this.onLocationScopeDivergence = config?.onLocationScopeDivergence;
        this.refreshIntervalSeconds = config?.refreshIntervalMinutes
            ? config.refreshIntervalMinutes * 60
            : 600;
    }
    /**
     * Initialize the authorizer and its provider
     * Loads permission tree and user context
     */
    async initialize() {
        await this.provider.initialize();
        this.permissionTree = await this.provider.getPermissionTree();
        if (!this.permissionTree) {
            throw new Error('Permission tree not found');
        }
        this.ability = this.buildAbilities();
        if (this.autoRefresh) {
            this.refreshInterval = setInterval(async () => {
                await this.refreshPermissions().catch((error) => {
                    // safely fail to not break client functionality - keep using cached permissions
                    // we will probably want to change this to be more robust at some point (i.e. a callback or retry mechanism)
                    // but for now, we'll just log the error and continue
                    console.error('Failed to refresh permissions:', error); // eslint-disable-line no-console
                });
            }, this.refreshIntervalSeconds * 1000);
        }
    }
    /**
     * Check if user has permission(s) to perform an operation on a resource
     * @param operation - Single operation or array of operations
     * @param resource - The resource to check against
     * @param options - Additional options for permission checking
     */
    can(operation, resource, options) {
        if (!this.ability) {
            throw new Error('Authorizer not initialized');
        }
        if (!operation || !operation.length)
            return false;
        if (options?.locationId === undefined) {
            return this.evaluate(this.ability, operation, resource, options);
        }
        const { locationId } = options;
        const legacyResult = this.evaluate(this.getLocationAbility(locationId), operation, resource, options);
        const isMember = this.isLocationMember(locationId);
        const scopedResult = isMember ? legacyResult : false;
        if (legacyResult !== scopedResult && this.onLocationScopeDivergence) {
            // Never let an observer fault (e.g. a metrics client throwing) turn an
            // authorization check into an uncaught exception. Isolate it so `can()`
            // always returns a boolean.
            try {
                this.onLocationScopeDivergence({
                    operations: Array.isArray(operation) ? [...operation] : [operation],
                    resource,
                    locationId,
                    legacyResult,
                    scopedResult,
                });
            }
            catch {
                // Swallow: observability is best-effort, authorization is not.
            }
        }
        return this.enforceLocationScope ? scopedResult : legacyResult;
    }
    canAtLocation(operation, resource, locationId) {
        return (this.isLocationMember(locationId) &&
            this.can(operation, resource, { locationId }));
    }
    evaluate(ability, operation, resource, options) {
        const operations = Array.isArray(operation) ? operation : [operation];
        const checks = [];
        for (const op of operations) {
            checks.push(ability.can(op, resource));
        }
        return options?.evaluation === 'some'
            ? checks.some((check) => check)
            : checks.every((check) => check);
    }
    /**
     * Whether the user holds any role at `locationId` — the real location-scoping
     * signal. A synthetic all-locations grant (`'*'`) counts as membership of
     * every location.
     */
    isLocationMember(locationId) {
        if (!this.permissionTree) {
            throw new Error('Authorizer not initialized');
        }
        const { rolesByLocation } = this.permissionTree;
        if ((rolesByLocation[SYNTHETIC_LOCATION_ID]?.length ?? 0) > 0) {
            return true;
        }
        return (rolesByLocation[locationId]?.length ?? 0) > 0;
    }
    buildAbilities() {
        const { can, build } = new AbilityBuilder(PureAbility);
        if (!this.permissionTree) {
            throw new Error('Permission tree not initialized');
        }
        const brandRoles = this.provider.getUserContext().brandRoles;
        for (const role of brandRoles) {
            const permissionIds = this.permissionTree.permissionsByRole[role] ?? [];
            for (const permId of permissionIds) {
                const permission = this.permissionTree.permissionsById[permId];
                if (!permission)
                    continue;
                can(caslOperation(permission.operation), permission.resource);
            }
        }
        return build();
    }
    getLocationAbility(locationId) {
        const ctx = this.provider.getUserContext();
        const cacheKey = `${ctx.userId}:${locationId}`;
        const cached = this.locationAbilitiesCache.get(cacheKey);
        if (cached) {
            return cached;
        }
        const ability = this.buildLocationAbilities(locationId);
        this.locationAbilitiesCache.set(cacheKey, ability);
        return ability;
    }
    buildLocationAbilities(locationId) {
        const { can, build } = new AbilityBuilder(PureAbility);
        if (!this.permissionTree) {
            throw new Error('Permission tree not initialized');
        }
        const ctx = this.provider.getUserContext();
        const brandRoles = ctx.brandRoles;
        const locationRoles = this.permissionTree.rolesByLocation[locationId] ?? [];
        const allRoles = [...brandRoles, ...locationRoles];
        for (const role of allRoles) {
            const permissionIds = this.permissionTree.permissionsByRole[role] ?? [];
            for (const permId of permissionIds) {
                const permission = this.permissionTree.permissionsById[permId];
                if (!permission)
                    continue;
                can(caslOperation(permission.operation), permission.resource);
            }
        }
        return build();
    }
    /**
     * Check if user has a specific role
     *
     * @param role - The role to check for
     * @param locationId - Optional location ID to check location-specific roles
     * @returns True if the user has the specified role
     */
    hasRole(role, locationId) {
        return this.hasRoles([role], { locationId });
    }
    hasRoles(roles, options) {
        if (!this.permissionTree) {
            throw new Error('Authorizer not initialized');
        }
        if (options.locationId) {
            const locationRoles = this.permissionTree.rolesByLocation[options.locationId] ?? [];
            return options.evaluation === 'some'
                ? roles.some((role) => locationRoles.includes(role))
                : roles.every((role) => locationRoles.includes(role));
        }
        const brandRoles = this.provider.getUserContext().brandRoles;
        return options.evaluation === 'some'
            ? roles.some((role) => brandRoles.includes(role))
            : roles.every((role) => brandRoles.includes(role));
    }
    /**
     * Get roles for a specific location
     *
     * @param locationId - The location ID to get roles for
     * @returns Array of role names for the specified location
     */
    getRolesForLocation(locationId) {
        if (!this.permissionTree) {
            return [];
        }
        return this.permissionTree.rolesByLocation[locationId] ?? [];
    }
    /**
     * Get the current user's ID
     */
    getUserId() {
        return this.provider.getUserContext().userId;
    }
    /**
     * Get the user's brand ID (null for super admins)
     */
    getBrandId() {
        return this.provider.getUserContext().brandId;
    }
    /**
     * Get the user's email (if available)
     */
    getEmail() {
        return this.provider.getUserContext().email;
    }
    /**
     * Get the location IDs the user is scoped to (empty array means unrestricted)
     */
    getLocationIds() {
        return this.provider.getUserContext().locationIds;
    }
    /**
     * Whether the caller belongs to the active brand at all (brand membership /
     * all-brands / internal), independent of which permissions they hold on it.
     * Lets a denial be classified as "not a member of this brand" vs. "a member
     * who lacks this specific permission". Returns `undefined` when the provider
     * cannot supply it (e.g. the legacy Olympus provider has no `my-access`
     * equivalent) — callers should treat `undefined` as "unknown", not "denied".
     */
    hasBrandAccess() {
        return this.provider.getUserContext().hasAccess;
    }
    /**
     * Get the authorization provider type
     *
     * @returns The provider type: 'mock' for mock/test provider or 'auth-service' for auth service
     */
    getAuthProvider() {
        return this.provider.name;
    }
    /**
     * Get the source provider (source of the permission tree)
     *
     * @returns The source provider: 'keycloak' for mock data or 'auth-service' for auth service tokens
     */
    getSourceProvider() {
        return this.provider.name === 'mock' ? 'keycloak' : 'auth-service';
    }
    /**
     * Clear all cached location-specific abilities
     */
    clearCache() {
        this.locationAbilitiesCache.clear();
    }
    /**
     * Force refresh the permission tree from provider
     */
    async refreshPermissions() {
        this.permissionTree = await this.provider.getPermissionTree();
        this.ability = this.buildAbilities();
        this.locationAbilitiesCache.clear();
    }
    /**
     * Clean up resources (stop auto-refresh interval and clear cache)
     * Call this when the authorizer is no longer needed
     */
    destroy() {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
            this.refreshInterval = undefined;
        }
        this.locationAbilitiesCache.clear();
    }
}
//# sourceMappingURL=index.js.map